import {useRef} from "react";
import {useSelector} from "react-redux";
import {Chart} from "react-chartjs-2";
import {getCurrentAsset} from "../../../ducks/assets-duck.js";
import {getRateHistoryForAsset} from "../../../ducks/history-duck.js";
import {CHART_PALETTE, getChartGradients} from "../chart/_chart-palette.js";
import {DECIMAL_FORMAT, numberFormat} from "../../../util/numbers.js";
import dayjs from "dayjs";
import './RateTrajectory.scss'

const MAX_DAYS = 365 + 31
const MIN_VISIBLE_DAYS = 31 // one month
const MAX_HIDDEN_RECENT_DAYS = 31

export default () => {

    const chartRef = useRef()
    const {currentRate, id} = useSelector(getCurrentAsset)
    const rateHistory = useSelector(state => getRateHistoryForAsset(state, id))

    const rateHistorySliced = rateHistory.filter(entry => entry.rate).slice(-MAX_DAYS)

    if (rateHistorySliced.length < 2) {
        return null
    }

    const trajectory = rateHistorySliced.map((entry, idx) => ({
        date: entry.date,
        relativeRateDiff: (currentRate - entry.rate) / entry.rate / (rateHistorySliced.length - idx) * 365 * 100,
    }))

    const hiddenRecentDays = Math.min(Math.max(rateHistorySliced.length - MIN_VISIBLE_DAYS, 0), MAX_HIDDEN_RECENT_DAYS)
    const visibleTrajectory = hiddenRecentDays > 0 ?
        trajectory.slice(0, -hiddenRecentDays) :
        trajectory

    const latestValue = visibleTrajectory[visibleTrajectory.length - 1].relativeRateDiff
    const isPositive = latestValue >= 0

    const {lineGradient, backgroundGradient} = getChartGradients(
        isPositive ? CHART_PALETTE.POSITIVE : CHART_PALETTE.NEGATIVE,
        chartRef
    )

    const data = {
        labels: visibleTrajectory.map(t => t.date),
        datasets: [{
            data: visibleTrajectory.map(t => t.relativeRateDiff),
            borderColor: lineGradient,
            backgroundColor: backgroundGradient,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: backgroundGradient,
            borderWidth: 2,
            fill: true,
            tension: 0.15,
        }],
    }

    const options = {
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            axis: 'x',
            intersect: false,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: items => dayjs(visibleTrajectory[items[0].dataIndex].date).format('D MMM YYYY'),
                    label: item => `${numberFormat(item.parsed.y, DECIMAL_FORMAT.FLEX_PERCENTAGE)}% annualized`,
                },
            },
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'month',
                    displayFormats: {
                        month: 'MMM',
                    },
                },
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                },
            },
            y: {
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                ticks: {
                    callback: value => `${value}%`,
                },
            },
        },
    }

    return (
        <div className='rate-trajectory hide-portrait'>
            <h3>Annualized rate trajectory</h3>
            <div className='chart-container'>
                <Chart
                    className='no-select'
                    type='line'
                    data={data}
                    options={options}
                    ref={chartRef}
                />
            </div>
        </div>
    )
}
